import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Archive, Send, RotateCcw, Loader2, CheckCircle2, XCircle, Clock, Activity } from "lucide-react";
import { api, faDigits, faNum } from "../lib/api";

export default function Backup() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restorePath, setRestorePath] = useState("");

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/backup/status");
      setStatus(data);
    } catch { /* silent */ }
  };
  useEffect(() => {
    loadStatus();
    // Poll faster when a backup is running
    const interval = status?.running ? 2000 : 10000;
    const t = setInterval(loadStatus, interval);
    return () => clearInterval(t);
  }, [status?.running]);

  const startBackup = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      if (data.started) {
        toast.success("بکاپ در پس‌زمینه شروع شد — پیشرفت را در همین صفحه ببینید");
      } else {
        toast.error(data.message || "خطا در آغاز بکاپ");
      }
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "خطا در اجرای بکاپ"); }
    finally { setRunning(false); }
  };

  const runRestore = async () => {
    if (!restorePath) return toast.error("مسیر فایل را وارد کنید");
    if (!window.confirm(`ریستور از مسیر «${restorePath}»؟`)) return;
    setRestoring(true);
    try {
      const { data } = await api.post("/admin/backup/restore", { path: restorePath });
      toast.success(`ریستور موفق — ${faNum(data.restored)} از ${faNum(data.total_in_file)} رکورد ذخیره شد`);
    } catch (e) { toast.error(e?.response?.data?.detail || "خطا در ریستور"); }
    finally { setRestoring(false); }
  };

  const lastRunText = status?.last_run_ts ? new Date(status.last_run_ts * 1000).toLocaleString("fa-IR") : "هنوز اجرا نشده";
  const progress = status?.progress || {};
  const pct = progress.total > 0 ? Math.min(100, Math.round((progress.processed / progress.total) * 100)) : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto space-y-6" data-testid="backup-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-700 border border-teal-200 grid place-items-center"><Archive size={22} /></div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">بکاپ و ریستور کش هویت‌ها</h1>
          <p className="text-sm text-slate-500 mt-1">استریمینگ + چند قسمتی — مناسب حجم بالا (میلیون‌ها رکورد)</p>
        </div>
      </div>

      {status?.running && (
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5 space-y-3" data-testid="progress-panel">
          <div className="flex items-center gap-2 text-teal-800 font-semibold">
            <Activity className="animate-pulse" size={18} />
            بکاپ در حال اجرا — {progress.status || "در حال آماده‌سازی"}
          </div>
          <div className="w-full bg-white rounded-full h-3 overflow-hidden border border-teal-200">
            <motion.div
              className="h-full bg-gradient-to-l from-teal-500 to-teal-600"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
          <div className="flex flex-wrap justify-between text-xs text-teal-800 gap-3">
            <span>پیشرفت: {faDigits(pct)}٪</span>
            <span>پردازش‌شده: {faNum(progress.processed || 0)} از {faNum(progress.total || 0)}</span>
            <span>قسمت فعلی: {faNum(progress.part || 0)}</span>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-6 space-y-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Clock size={18} /> وضعیت زمان‌بندی</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <InfoCard label="بکاپ خودکار" value={status?.enabled ? "فعال" : "غیرفعال"} tone={status?.enabled ? "green" : "slate"} />
          <InfoCard label="بازه" value={status ? `هر ${faDigits(status.interval_hours)} ساعت` : "-"} tone="indigo" />
          <InfoCard label="اندازه هر قسمت" value={status ? `${faNum(status.chunk_size)} رکورد` : "-"} tone="indigo" />
          <InfoCard label="آخرین اجرا" value={lastRunText} tone="slate" />
        </div>
        {status?.last_status && (
          <div className="text-xs flex items-start gap-2 pt-2 border-t border-slate-100">
            {String(status.last_status).startsWith("success") ? <CheckCircle2 size={14} className="text-emerald-600 mt-0.5" /> : <XCircle size={14} className="text-rose-600 mt-0.5" />}
            <span className="text-slate-600">آخرین وضعیت: {status.last_status}</span>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-6 space-y-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Send size={18} /> بکاپ دستی</h3>
        <p className="text-xs text-slate-500 leading-6">
          داده‌ها به‌صورت استریم از دیتابیس خوانده می‌شوند (بدون بارگذاری کل جدول)، در قطعات
          <b> {faNum(status?.chunk_size || 200000)}</b>‌رکوردی به CSV و سپس زیپ رمزدار AES-256 تبدیل و به بله ارسال می‌شوند.
          سرور در حین اجرا پاسخگوی سایر درخواست‌ها می‌ماند. اندازه هر قسمت را از صفحه <b>تنظیمات</b> می‌توانید تغییر دهید.
        </p>
        <button
          data-testid="backup-run-button"
          onClick={startBackup}
          disabled={running || status?.running}
          className="px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2 shadow-sm active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {running || status?.running ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {status?.running ? "در حال اجرا..." : "شروع بکاپ در پس‌زمینه"}
        </button>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-6 space-y-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2"><RotateCcw size={18} /> ریستور از فایل روی سرور</h3>
        <p className="text-xs text-slate-500">
          مسیر یک فایل بکاپ (زیپ رمزدار یا CSV/JSON استخراج‌شده) روی سرور را وارد کنید. برای بکاپ چند‌قسمتی این عملیات را به‌ازای هر قسمت اجرا کنید.
        </p>
        <div className="flex flex-col md:flex-row gap-2">
          <input data-testid="restore-path-input" type="text" value={restorePath} onChange={(e) => setRestorePath(e.target.value)}
            placeholder="/root/backups/identity_backup_14030515_103000_part0001.zip"
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 outline-none text-sm font-mono" />
          <button data-testid="restore-run-button" onClick={runRestore} disabled={restoring}
            className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2 shadow-sm active:scale-[0.98]">
            {restoring ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
            اجرای ریستور
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function InfoCard({ label, value, tone }) {
  const tones = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-xs opacity-75">{label}</div>
      <div className="text-sm font-semibold mt-1">{value}</div>
    </div>
  );
}
